import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expValEquals } from '@confluence/feature-experiments';

/**
 * Hook that intercepts the space key shortcut in the editor when
 * the cwr_blank_object_experiment is variant1.
 *
 * Normally, pressing space in an empty editor opens inline Rovo.
 * When this hook is active, it captures the space keydown event in
 * the capture phase (before ProseMirror handles it) and instead
 * triggers the Create With Rovo experience.
 *
 * This hook is intentionally implemented as a DOM capture-phase listener
 * rather than modifying the editor plugin, so that all experiment-specific
 * code stays in the Confluence layer and can be easily removed if the
 * experiment doesn't go to production. If it does go to production, we will work
 * with editor team to determine space key functionality long-term.
 */
export function useSpaceKeyShortcutOverride({
	isActive,
	isRovoLLMEnabled,
	onSpaceKeyPressed,
	isLivePage,
}: {
	/** Whether the shortcut override should be active (e.g. blank page quick actions are showing, prompt not yet submitted) */
	isActive: boolean;
	/** Whether Rovo LLM is enabled for the current user */
	isRovoLLMEnabled: boolean;
	/** Callback to invoke when the space key is pressed (e.g. show the CWR experience) */
	onSpaceKeyPressed: () => void;
	/** Whether the current page is a live doc */
	isLivePage: boolean;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onSpaceKeyPressedRef = useRef(onSpaceKeyPressed);
	onSpaceKeyPressedRef.current = onSpaceKeyPressed;

	useEffect(() => {
		if (
			!isActive ||
			!isRovoLLMEnabled ||
			!expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1')
		) {
			return;
		}

		const handleSpaceKeyCapture = (e: KeyboardEvent) => {
			// Only intercept the bare space key (no modifiers)
			if (e.key !== ' ' || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
				return;
			}

			// Only intercept when the target is within the ProseMirror editor
			const target = e.target as HTMLElement;
			if (!target?.closest?.('.ProseMirror')) {
				return;
			}

			// Prevent ProseMirror from handling the space key (which would open inline Rovo)
			e.preventDefault();
			e.stopPropagation();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'BlankObjectExperienceButtons',
					action: 'used',
					actionSubject: 'shortcut',
					actionSubjectId: 'createWithRovoBlankObject',
					attributes: {
						variant: 'variant1',
						contentType: isLivePage ? 'liveDoc' : 'page',
						buttonType: 'cwr',
					},
				},
			}).fire();

			// Show the CWR experience
			onSpaceKeyPressedRef.current();
		};

		// Use capture phase to intercept before ProseMirror's keymap handler
		document.addEventListener('keydown', handleSpaceKeyCapture, true);
		return () => {
			document.removeEventListener('keydown', handleSpaceKeyCapture, true);
		};
	}, [isActive, isRovoLLMEnabled, createAnalyticsEvent, isLivePage]);
}
